import React, { Fragment, memo, useCallback, useMemo, useState } from 'react'
import tw from 'twin.macro'
import { CircleRightMajor, MobileHamburgerMajor } from '@shopify/polaris-icons'
import { Button } from '../shared/Button'
import { MobileMenu } from './MobileMenu'
import { LogoutModal } from './LogoutModal'
import { useSelector } from 'react-redux'
import { getContractorPortalPartners } from 'src/selectors/contractorPortal'
import { getIsPartnerAccessRestricted } from 'src/selectors/partner'
import RecollectIcon from 'src/assets/icons/recollect-white.svg'
import { SwitchPartnerModal } from './SwitchPartnerModal'
import { show as showIntercom } from '@intercom/messenger-js-sdk'
import { useAuthTokens } from 'src/hooks/useAuthTokens'

interface Props {
  flow?: 'client' | 'contractorPortal'
  variant?: 'light' | 'dark'
  hideSwitchButtons?: boolean
}

const SettingsComponent = ({ flow, variant = 'dark', hideSwitchButtons }: Props) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [isLogoutOpen, setIsLogoutOpen] = useState(false)
  const [isSwitchPartnerOpen, setIsSwitchPartnerOpen] = useState(false)

  const { authToken: clientAuthToken, contractorPortalAuthToken } = useAuthTokens()

  const contractorPortalPartners = useSelector(getContractorPortalPartners)
  const isPartnerAccessRestricted = useSelector(getIsPartnerAccessRestricted)

  const authToken = useMemo(() => {
    if (flow === 'client') {
      return clientAuthToken
    }

    if (flow === 'contractorPortal') {
      return contractorPortalAuthToken
    }

    return null
  }, [flow, clientAuthToken, contractorPortalAuthToken])

  const showSwitchPartner = useMemo(
    () => flow === 'contractorPortal' && contractorPortalPartners.length > 1 && !hideSwitchButtons,
    [flow, contractorPortalPartners.length, hideSwitchButtons],
  )

  const showBenefitPortal = useMemo(
    () => flow !== 'contractorPortal' || !isPartnerAccessRestricted,
    [flow, isPartnerAccessRestricted],
  )

  const menuTestIdPrefix = useMemo(() => {
    if (flow === 'contractorPortal') {
      return 'contractors-list'
    }

    if (flow === 'client') {
      return 'customer-interface'
    }

    return 'sign-in'
  }, [flow])

  const openMenu = useCallback(() => {
    setIsMenuOpen(true)
  }, [])

  const closeMenu = useCallback(() => {
    setIsMenuOpen(false)
  }, [])

  const openLogout = useCallback(() => {
    setIsLogoutOpen(true)
  }, [])

  const closeLogout = useCallback(() => {
    setIsLogoutOpen(false)
  }, [])

  const handleGetHelp = useCallback(() => {
    showIntercom()
  }, [])

  const openSwitchPartner = useCallback(() => {
    setIsSwitchPartnerOpen(true)
  }, [])

  const closeSwitchPartner = useCallback(() => {
    setIsSwitchPartnerOpen(false)
    setIsMenuOpen(false)
  }, [])

  return (
    <Fragment>
      {authToken && (
        <Button
          leftIcon={
            <MobileHamburgerMajor
              css={[tw`mx-1`, variant === 'light' ? tw`fill-primary-500` : tw`fill-shades-0`]}
              width={20}
              height={20}
            />
          }
          onClick={openMenu}
          size="medium"
          color={variant === 'light' ? 'light' : 'primary'}
          className={tw`block sm:hidden`}
          data-testid={`${menuTestIdPrefix}-menu-btn`}
        />
      )}
      <div css={tw`sm:block hidden`}>
        {showSwitchPartner ? (
          <Button
            leftIcon={<img src={RecollectIcon} />}
            color="dark"
            size="medium"
            onClick={openSwitchPartner}
            data-testid={`${menuTestIdPrefix}-switch-partner-btn`}
          >
            Switch Partner
          </Button>
        ) : null}
        {authToken && (
          <Button
            leftIcon={<CircleRightMajor fill="white" width={20} height={20} />}
            size="medium"
            onClick={openLogout}
            className={tw`ml-4`}
            data-testid={`${menuTestIdPrefix}-log-out-btn`}
          >
            Log out
          </Button>
        )}
      </div>
      {authToken && (
        <MobileMenu
          isOpen={isMenuOpen}
          onClose={closeMenu}
          onGetHelp={handleGetHelp}
          onLogout={authToken ? openLogout : undefined}
          onSwitchPartner={showSwitchPartner ? openSwitchPartner : undefined}
          menuTestIdPrefix={menuTestIdPrefix}
          showBenefitPortal={showBenefitPortal}
        />
      )}
      <LogoutModal flow={flow} isOpen={isLogoutOpen} onClose={closeLogout} />
      <SwitchPartnerModal isOpen={isSwitchPartnerOpen} onClose={closeSwitchPartner} />
    </Fragment>
  )
}

export const Settings = memo(SettingsComponent)
